import React, { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  SkeletonText,
  IconButton,
  Box,
  FormLabel,
  Input,
  Button,
  Tooltip,
  FormErrorMessage,
  FormControl,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { dateFormat, FORMATS, getOrganisationMembership } from "utils";
import { Roles } from "common";
import { CheckCircleIcon, CopyIcon } from "@chakra-ui/icons";
import {
  useGetOrganizationQuery,
  useGetProfileQuery,
  useUpdateWebhookMutation,
} from "redux/services/membership";
import { FiEye, FiEyeOff } from "react-icons/fi";
import useCustomToast from "hooks/useCustomToast";
import { Controller, FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { EditIcon } from "components/Icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { webhookSchema } from "pages/APICredentials/schema";
import { UpdateWebhookRequest } from "redux/services/membership/types";
import env from "env";

interface CustomField {
  customFieldId: string;
  orgId: string;
  customKey: string;
  customValue: string;
}

const APICredentials: React.FC = () => {
  const { data: userData } = useGetProfileQuery();
  const [isClientSecretVisible, setIsClientSecretVisible] = useState(false);
  const [isCopiedClientSecrect, setIsCopiedClientSecrect] = useState(false);
  const [isCopiedCLientId, setIsCopiedCLientId] = useState(false);
  const [isHMACSecretVisible, setIsHMACSecretVisible] = useState(false);
  const [isCopiedHMACSecret, setIsCopiedHMACSecret] = useState(false);
  const [defaultEndpoint, setDefaultEndpoint] = useState("");
  const [defaultHMACSecret, setDefaultHMACSecret] = useState("");
  const [isWebHookEditable, setIsWebHookEditable] = useState(false);

  const { showToast } = useCustomToast();

  const isEnterpriseBankAdmin = useMemo(
    () => userData?.data?.listRoles?.includes(Roles.EnterpriseBankAdmin),
    [userData?.data?.listRoles]
  );

  const [updateWebhook, { isLoading: isLoadingUpdateWebhook }] =
    useUpdateWebhookMutation();

  const membershipOrganisation = getOrganisationMembership(
    userData?.data?.memberships
  );

  const orgId = useMemo(
    () => membershipOrganisation?.organisationId || "",
    [membershipOrganisation]
  );

  const {
    data: organizationData,
    isFetching: isfetchOrganization,
    isSuccess: isFetchSuccess,
  } = useGetOrganizationQuery(
    {
      orgId,
      userId: userData?.data?.userId || "",
    },
    {
      skip: !(userData && membershipOrganisation && !isEnterpriseBankAdmin),
      refetchOnMountOrArgChange: true,
    }
  );

  const defaultWebhookValues = useMemo(
    () => ({
      Dev_Webhook_Url: defaultEndpoint,
      Dev_Webhook_Signing_Key: defaultHMACSecret,
    }),
    [defaultEndpoint, defaultHMACSecret]
  );

  const form = useForm({
    mode: "onSubmit",
    resolver: yupResolver(webhookSchema),
    defaultValues: defaultWebhookValues,
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = form;

  const handleCopyClientSecret = () => {
    showToast("success", "Client Secret copied to clipboard");
    setIsCopiedClientSecrect(true);

    clientSecret && navigator.clipboard.writeText(clientSecret);

    setTimeout(() => {
      setIsCopiedClientSecrect(false);
    }, 2000);
  };

  const handleCopyClientId = () => {
    showToast("success", "Client ID copied to clipboard");
    setIsCopiedCLientId(true);

    clientId && navigator.clipboard.writeText(clientId);

    setTimeout(() => {
      setIsCopiedCLientId(false);
    }, 2000);
  };

  const handleSave = async (data: any) => {
    try {
      const payload: UpdateWebhookRequest = {
        orgId,
        customFields: [
          {
            customKey: "Dev_Webhook_Url",
            customValue: data.Dev_Webhook_Url,
          },
          {
            customKey: "Dev_Webhook_Signing_Key",
            customValue: data.Dev_Webhook_Signing_Key,
          },
          {
            customKey: "Dev_Webhook_Event_Code",
            customValue: env.app.paymentWebhookEventCode,
          },
        ],
      };

      const res: any = await updateWebhook(payload);

      if ("error" in res) {
        const errorMessage =
          (res as any)?.error?.data?.errors?.[0]?.message ??
          "Failed to registered endpoint.";
        showToast("error", errorMessage);

        return;
      }

      const isInitialRegistration =
        defaultEndpoint === "" && defaultHMACSecret === "";

      showToast(
        "success",
        `Endpoint successfully ${
          isInitialRegistration ? "registered" : "updated"
        }.`
      );

      setIsWebHookEditable(false);
      setIsHMACSecretVisible(false);
      setDefaultEndpoint(data.Dev_Webhook_Url);
      setDefaultHMACSecret(data.Dev_Webhook_Signing_Key);
    } catch (error) {
      showToast("error", "Failed to registered endpoint.");
      console.log("error", error);
    }
  };

  const handleCopyHMACSecret = () => {
    showToast("success", "HMAC copied to clipboard");
    setIsCopiedHMACSecret(true);
    navigator.clipboard.writeText(form.getValues("Dev_Webhook_Signing_Key"));

    setTimeout(() => {
      setIsCopiedHMACSecret(false);
    }, 2000);
  };

  const handleCancel = () => {
    if (defaultEndpoint === "" && defaultHMACSecret === "") {
      form.reset(defaultWebhookValues);
      return;
    }

    setIsWebHookEditable(false);
    setIsHMACSecretVisible(false);
    reset(defaultWebhookValues);
  };

  const clientId =
    (
      organizationData?.data?.customFields?.find(
        (field: CustomField) => field.customKey === "Dev_Backend_ApiKey"
      ) as unknown as CustomField
    )?.customValue || undefined;
  const clientSecret =
    (
      organizationData?.data?.customFields?.find(
        (field: CustomField) => field.customKey === "Dev_Backend_ApiSecret"
      ) as unknown as CustomField
    )?.customValue || undefined;
  const createdAt = dateFormat(
    organizationData?.data.createdAt,
    true,
    FORMATS["YYYY-MM-DD HH:mm"]
  );

  useEffect(() => {
    setDefaultEndpoint(
      (
        organizationData?.data?.customFields?.find(
          (field: CustomField) => field.customKey === "Dev_Webhook_Url"
        ) as unknown as CustomField
      )?.customValue || ""
    );

    setDefaultHMACSecret(
      (
        organizationData?.data?.customFields?.find(
          (field: CustomField) => field.customKey === "Dev_Webhook_Signing_Key"
        ) as unknown as CustomField
      )?.customValue || ""
    );
  }, [organizationData?.data?.customFields]);

  useEffect(() => {
    reset(defaultWebhookValues);
    setIsWebHookEditable(
      defaultWebhookValues.Dev_Webhook_Url === "" &&
        defaultWebhookValues.Dev_Webhook_Signing_Key === ""
    );
  }, [defaultWebhookValues, reset]);

  const isLoading = isfetchOrganization || !isFetchSuccess;
  const handleCopyOrganizationId = () => {
    navigator.clipboard.writeText(userData?.data.memberships?.[0]?.organisationId || "N/A");
    showToast("success", "Parent Organization ID copied to clipboard");
  };
  return (
    <Flex
      className="Users"
      w="100%"
      h="100%"
      p="20px"
      flexDir={"column"}
      position="relative"
      minHeight="calc(100vh - 124px)"
    >
      <Text textStyle={"h1"} fontWeight={"extrabold"}>
        API Credentials
      </Text>
      <Text textStyle={"h3"} mt="20px">
        <b>Parent Organisation ID:</b>{" "}
        {userData?.data.memberships?.[0]?.organisationId || "N/A"}{" "}
        <IconButton
          variant="unstyled"
          aria-label="copy-orgId"
          className="!focus:shadow-none !shadow-none"
          icon={<CopyIcon />}
          onClick={handleCopyOrganizationId}
        />
      </Text>
      <TableContainer mt="20px" pb="20px">
        <Table data-testid="table-container" variant="striped">
          <Thead backgroundColor={"yellow.500"}>
            <Tr>
              <Th color={"white"}>Created At(UTC+00:00)</Th>
              <Th color={"white"}>Client ID</Th>
              <Th color={"white"}>Client Secret</Th>
              <Th color={"white"}>Status</Th>
            </Tr>
          </Thead>

          {isLoading && (
            <Tr>
              <Td colSpan={4}>
                <SkeletonText
                  data-testid="skeleton-text"
                  noOfLines={1}
                  spacing="10px"
                  p="10px 0"
                />
              </Td>
            </Tr>
          )}

          {!isLoading && organizationData?.data?.customFields && (
            <Tbody>
              {organizationData?.data?.customFields?.length > 0 && (
                <Tr>
                  <Td textAlign={"left"}>{createdAt}</Td>
                  <Td textAlign={"left"}>
                    {clientId}
                    <IconButton
                      variant="unstyled"
                      aria-label="copy-clientId"
                      className="!focus:shadow-none !shadow-none"
                      icon={
                        !isCopiedCLientId ? <CopyIcon /> : <CheckCircleIcon />
                      }
                      onClick={handleCopyClientId}
                    />
                  </Td>
                  <Td
                    textAlign={"left"}
                    flexDirection={"row"}
                    display={"flex"}
                    alignItems={"center"}
                  >
                    <Box
                      overflow={"hidden"}
                      width={192}
                      textOverflow={"ellipsis"}
                    >
                      {isClientSecretVisible
                        ? clientSecret
                        : "*****************************"}
                    </Box>
                    <IconButton
                      variant="unstyled"
                      aria-label="copy-clientSecret"
                      data-testid={
                        isCopiedClientSecrect ? "copied-icon" : "copy-icon"
                      }
                      className="!focus:shadow-none !shadow-none"
                      icon={
                        !isCopiedClientSecrect ? (
                          <CopyIcon />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                      onClick={handleCopyClientSecret}
                    />
                    <IconButton
                      display={"inline-grid"}
                      variant="unstyled"
                      aria-label="eyes-show-secret"
                      className="!focus:shadow-none !shadow-none"
                      icon={isClientSecretVisible ? <FiEye /> : <FiEyeOff />}
                      onClick={() => {
                        setIsClientSecretVisible((prev) => !prev);
                      }}
                    />
                  </Td>
                  <Td textAlign={"left"}>Active</Td>
                </Tr>
              )}
            </Tbody>
          )}
        </Table>
      </TableContainer>

      {!isLoading && organizationData?.data?.customFields?.length === 0 && (
        <Text p="20px" textAlign="center" fontWeight="bold">
          No records found
        </Text>
      )}

      <TableContainer mt="20px" pb="20px">
        <Table data-testid="table-webhook" variant="striped">
          <Thead backgroundColor={"yellow.500"}>
            <Tr>
              <Th color={"white"}>Webhook Notification Settings</Th>
            </Tr>
          </Thead>

          {isLoading && (
            <Tr>
              <Td colSpan={1}>
                <SkeletonText
                  data-testid="skeleton-text"
                  noOfLines={1}
                  spacing="10px"
                  p="10px 0"
                />
              </Td>
            </Tr>
          )}

          {!isLoading && (
            <Tbody>
              <Tr>
                <Td>
                  <FormProvider {...form}>
                    <form
                      onSubmit={handleSubmit(handleSave)}
                      className="flex gap-5 justify-between xl:flex-row flex-col"
                    >
                      <FormControl
                        isInvalid={!!errors.Dev_Webhook_Url}
                        className="flex lg:flex-1"
                      >
                        <FormLabel className="block min-w-[110px] xl:min-w-auto !leading-[42px]">
                          Endpoint <span className="text-red-500">*</span>
                        </FormLabel>
                        <div className="w-full">
                          <Controller
                            name="Dev_Webhook_Url"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="https://example.com"
                                isDisabled={!isWebHookEditable}
                                fontSize="14px"
                                color="black"
                                backgroundColor="white"
                                autoComplete="off"
                                className={clsx("!h-[42px]")}
                                defaultValue={defaultEndpoint}
                              />
                            )}
                          />
                          <FormErrorMessage>
                            {errors.Dev_Webhook_Url?.message}
                          </FormErrorMessage>
                        </div>
                      </FormControl>

                      <div className="flex gap-1 lg:flex-1">
                        <FormControl
                          isInvalid={!!errors.Dev_Webhook_Signing_Key}
                          className="flex"
                        >
                          <FormLabel className="block min-w-[110px] xl:min-w-auto !leading-[42px]">
                            HMAC Secret <span className="text-red-500">*</span>
                          </FormLabel>
                          <div className="w-full">
                            <InputGroup>
                              <Controller
                                name="Dev_Webhook_Signing_Key"
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    isDisabled={!isWebHookEditable}
                                    fontSize="14px"
                                    color="black"
                                    backgroundColor={"white"}
                                    autoComplete={"off"}
                                    paddingEnd={
                                      isWebHookEditable ? "55px" : "16px"
                                    }
                                    className={clsx("!h-[42px]")}
                                    type={
                                      isHMACSecretVisible ? "text" : "password"
                                    }
                                    defaultValue={defaultHMACSecret}
                                  />
                                )}
                              />
                              {isWebHookEditable && (
                                <InputRightElement width="3rem">
                                  <IconButton
                                    variant="unstyled"
                                    aria-label="toggle-hmac-visibility"
                                    className="!focus:shadow-none !shadow-none"
                                    icon={
                                      isHMACSecretVisible ? (
                                        <FiEye />
                                      ) : (
                                        <FiEyeOff />
                                      )
                                    }
                                    onClick={() =>
                                      setIsHMACSecretVisible((prev) => !prev)
                                    }
                                  />
                                </InputRightElement>
                              )}
                            </InputGroup>
                            <FormErrorMessage>
                              {errors.Dev_Webhook_Signing_Key?.message}
                            </FormErrorMessage>
                          </div>
                        </FormControl>
                      </div>

                      <div className="flex gap-2 flex-col xl:flex-row">
                        {!isWebHookEditable && (
                          <div className="xl:w-[200px] text-center xl:text-left">
                            <Tooltip
                              label="Copy HMAC Secret"
                              fontSize="sm"
                              placement="bottom"
                            >
                              <IconButton
                                variant="unstyled"
                                aria-label="copy-HMACSecret"
                                className="!focus:shadow-none !shadow-none"
                                icon={
                                  !isCopiedHMACSecret ? (
                                    <CopyIcon />
                                  ) : (
                                    <CheckCircleIcon />
                                  )
                                }
                                onClick={handleCopyHMACSecret}
                                disabled={
                                  !form.getValues("Dev_Webhook_Signing_Key")
                                }
                              />
                            </Tooltip>
                            <Tooltip
                              label={
                                isHMACSecretVisible
                                  ? "Hide HMAC Secret"
                                  : "View HMAC Secret"
                              }
                              fontSize="sm"
                              placement="bottom"
                            >
                              <IconButton
                                display={"inline-grid"}
                                variant="unstyled"
                                aria-label="eyes-show-secret"
                                className="!focus:shadow-none !shadow-none"
                                icon={
                                  isHMACSecretVisible ? <FiEye /> : <FiEyeOff />
                                }
                                onClick={() => {
                                  setIsHMACSecretVisible((prev) => !prev);
                                }}
                              />
                            </Tooltip>

                            <Tooltip
                              label={
                                isWebHookEditable ? "Disable Edit" : "Edit"
                              }
                              fontSize="sm"
                              placement="bottom"
                            >
                              <IconButton
                                display={"inline-grid"}
                                variant="unstyled"
                                aria-label="btn-edit-webhook"
                                className="!focus:shadow-none !shadow-none"
                                icon={<EditIcon color="black" />}
                                onClick={() => {
                                  setIsWebHookEditable(true);
                                  if (isHMACSecretVisible) {
                                    setIsHMACSecretVisible(false);
                                  }
                                }}
                              />
                            </Tooltip>
                          </div>
                        )}

                        {isWebHookEditable && (
                          <>
                            <Button
                              disabled={isLoadingUpdateWebhook}
                              variant="outline"
                              type="button"
                              className="bg-white"
                              minW={"96px"}
                              _hover={{ bg: "gray.200" }}
                              _active={{ bg: "gray.300" }}
                              _focus={{ boxShadow: "none" }}
                              onClick={handleCancel}
                            >
                              Cancel
                            </Button>
                            <Button
                              isLoading={isLoadingUpdateWebhook}
                              disabled={isLoadingUpdateWebhook}
                              colorScheme="blue"
                              type="submit"
                              minW={"96px"}
                            >
                              Save
                            </Button>
                          </>
                        )}

                        <Button
                          isLoading={false}
                          disabled
                          colorScheme="blue"
                          type="button"
                          minW={"96px"}
                        >
                          Test
                        </Button>
                      </div>
                    </form>
                  </FormProvider>
                </Td>
              </Tr>
            </Tbody>
          )}
        </Table>
      </TableContainer>
    </Flex>
  );
};

export default APICredentials;
